import React, { useContext, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { AuthContext } from '../contexts/AuthContext';
import { getUserPhotoUrl } from '../utils/userPhoto';
import { HR_GESTAO_NF_LINK, HR_GESTAO_PONTO_LINK, useHrGestaoInbox } from '../hooks/useHrGestaoInbox';
import { useDeadlineAlerts } from '../hooks/useDeadlineAlerts';
import {
  deadlineAlertChipLabel,
  deadlineAlertKey,
  isInvoiceDeadlineAlert,
} from '../utils/deadlineAlertIdentity';
import { useStandupPendencias } from '../hooks/useStandupPendencias';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  id: string;
  label: string;
  to: string;
  end?: boolean;
  notify?: number;
  visible: boolean;
  icon: React.ReactNode;
}

const RH_SECTION_PATHS = ['/behavioral', '/desenvolvimento-pessoal', '/financeiro'];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const { user, logout } = authContext || {};
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);

  const { pendingInvoices, totalPending } = useHrGestaoInbox();
  const { alerts: deadlineAlerts, totalAlerts: deadlineAlertCount } = useDeadlineAlerts();
  const { missingStandups } = useStandupPendencias();

  const feedbackDeadlineAlert = deadlineAlerts.find((a) => a.kind === 'feedback') ?? null;
  const invoiceDeadlineAlert = deadlineAlerts.find((alert) => isInvoiceDeadlineAlert(alert)) ?? null;
  const hasGestaoPontoNotify = totalPending > 0;
  const totalNotifyCount = (hasGestaoPontoNotify ? totalPending : 0) + deadlineAlertCount;

  const userInitials = useMemo(() => {
    if (!user?.name) return 'F';
    return user.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }, [user?.name]);

  const roleLabel = useMemo(() => {
    const levelLabel: Record<string, string> = {
      junior: 'Júnior',
      pleno: 'Pleno',
      senior: 'Sênior',
    };
    switch (user?.role) {
      case 'admin':
        return 'Administrador';
      case 'manager':
        return 'Gestor';
      case 'developer': {
        const lvl = user?.level ? levelLabel[user.level] : '';
        return lvl ? `Desenvolvedor ${lvl}` : 'Desenvolvedor';
      }
      case 'hr':
        return 'RH';
      default:
        return 'Equipe';
    }
  }, [user?.role, user?.level]);

  const userPhotoUrl = useMemo(() => getUserPhotoUrl(user), [user]);

  const showRhAdminLinks = user?.role === 'admin' || user?.role === 'hr';
  const showGestaoPontoLink =
    user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';
  const showCyclesNav = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';
  const showReportsNav = user?.role !== 'developer';
  const showUsersNav = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'developer';

  const isRhSectionActive = RH_SECTION_PATHS.some((p) => location.pathname.startsWith(p));
  const rhNotify = totalPending + pendingInvoices;

  const mainNav: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', to: '/', end: true, visible: true, icon: <DashboardOutlinedIcon /> },
    {
      id: 'feedbacks',
      label: 'Feedbacks',
      to: '/feedbacks',
      notify: feedbackDeadlineAlert ? 1 : 0,
      visible: true,
      icon: <RateReviewOutlinedIcon />,
    },
    {
      id: 'ponto',
      label: 'Ponto',
      to: '/ponto-eletronico',
      visible: true,
      icon: <AccessTimeOutlinedIcon />,
    },
    {
      id: 'nf',
      label: 'Notas fiscais',
      to: '/notas-fiscais',
      notify: invoiceDeadlineAlert ? 1 : 0,
      visible: true,
      icon: <ReceiptLongOutlinedIcon />,
    },
    { id: 'standups', label: 'Stand-up', to: '/standups', notify: user?.role === 'developer' ? missingStandups : 0, visible: true, icon: <ViewDayOutlinedIcon /> },
    { id: 'cycles', label: 'Ciclos', to: '/cycles', visible: showCyclesNav, icon: <EventRepeatOutlinedIcon /> },
    { id: 'reports', label: 'Relatórios', to: '/reports', visible: showReportsNav, icon: <AssessmentOutlinedIcon /> },
    {
      id: 'users',
      label: user?.role === 'developer' ? 'Meu perfil' : 'Usuários',
      to: '/users',
      visible: showUsersNav,
      icon: user?.role === 'developer' ? <PersonOutlineIcon /> : <GroupOutlinedIcon />,
    },
  ].filter((item) => item.visible);

  const navItemActive = (item: NavItem): boolean =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);

  // "RH" dropdown now holds only management/HR-admin screens (self-service
  // Ponto / Notas fiscais are promoted to top-level items above).
  const rhItems = useMemo(() => {
    const items: { label: string; to: string; notify?: number; icon: React.ReactNode }[] = [];
    if (showGestaoPontoLink) {
      items.push({ label: 'Gestão de ponto', to: HR_GESTAO_PONTO_LINK, notify: totalPending, icon: <FactCheckOutlinedIcon /> });
      items.push({ label: 'Notas fiscais (revisão)', to: HR_GESTAO_NF_LINK, notify: pendingInvoices, icon: <ReceiptLongOutlinedIcon /> });
    }
    if (showRhAdminLinks) {
      items.push({ label: 'Financeiro', to: '/financeiro', icon: <AccountBalanceWalletOutlinedIcon /> });
      items.push({ label: 'Perfil Comportamental', to: '/behavioral', icon: <PsychologyOutlinedIcon /> });
      items.push({ label: 'Desenvolvimento Pessoal', to: '/desenvolvimento-pessoal', icon: <TrendingUpOutlinedIcon /> });
    }
    return items;
  }, [pendingInvoices, showGestaoPontoLink, showRhAdminLinks, totalPending]);

  const handleLogout = (): void => {
    setUserAnchor(null);
    setMobileOpen(false);
    if (logout) logout();
    navigate('/login');
  };

  const go = (to: string): void => {
    setMobileOpen(false);
    setMoreAnchor(null);
    setUserAnchor(null);
    navigate(to);
  };

  const year = new Date().getFullYear();

  const navButtonSx = (active: boolean) => ({
    color: active ? 'primary.main' : 'text.primary',
    fontWeight: 700,
    borderRadius: 0,
    borderBottom: '2px solid',
    borderColor: active ? 'primary.main' : 'transparent',
    px: 1,
    minWidth: 0,
    flexShrink: 0,
    '&:hover': { backgroundColor: 'action.hover', borderColor: active ? 'primary.main' : 'divider' },
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ gap: 1, px: { xs: 1.5, sm: 3 } }}>
            {/* Brand */}
            <Box
              component={NavLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: 'text.primary',
                mr: 2,
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src="/brand-logo.png"
                alt="Aplopes Tecnologia"
                sx={{ height: 32, width: 'auto', display: 'block' }}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1 }}>
                <Typography component="span" sx={{ fontWeight: 800, display: 'block', fontSize: '1rem' }}>
                  Feedback
                </Typography>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: { md: 'none', xl: 'block' } }}>
                  Gestão de performance
                </Typography>
              </Box>
            </Box>

            {/* Desktop nav */}
            <Box
              component="nav"
              aria-label="Navegação principal"
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.25,
                flexGrow: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {mainNav.map((item) => {
                const active = navItemActive(item);
                return (
                  <Button
                    key={item.id}
                    component={NavLink}
                    to={item.to}
                    end={item.end}
                    sx={navButtonSx(active)}
                  >
                    {item.notify ? (
                      <Badge color="error" badgeContent={item.notify} sx={{ '& .MuiBadge-badge': { right: -10 } }}>
                        {item.label}
                      </Badge>
                    ) : (
                      item.label
                    )}
                  </Button>
                );
              })}
              {rhItems.length > 0 && (
                <Button
                  onClick={(e) => setMoreAnchor(e.currentTarget)}
                  endIcon={<ExpandMoreIcon />}
                  aria-haspopup="true"
                  aria-expanded={Boolean(moreAnchor)}
                  sx={navButtonSx(isRhSectionActive)}
                >
                  {rhNotify > 0 ? (
                    <Badge color="error" badgeContent={rhNotify > 9 ? '9+' : rhNotify} sx={{ '& .MuiBadge-badge': { right: -10 } }}>
                      RH
                    </Badge>
                  ) : (
                    'RH'
                  )}
                </Button>
              )}
            </Box>

            <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

            {/* User (desktop) */}
            {user && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, flexShrink: 0, ml: 1 }}>
                <Button
                  onClick={(e) => setUserAnchor(e.currentTarget)}
                  aria-haspopup="true"
                  aria-expanded={Boolean(userAnchor)}
                  aria-label="Abrir menu da conta"
                  sx={{ textTransform: 'none', color: 'text.primary', gap: 1, px: 1 }}
                >
                  <Badge
                    color="error"
                    badgeContent={totalNotifyCount > 9 ? '9+' : totalNotifyCount}
                    invisible={totalNotifyCount === 0}
                    overlap="circular"
                  >
                    <Avatar src={userPhotoUrl || undefined} sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: '0.9rem', fontWeight: 700 }}>
                      {userInitials}
                    </Avatar>
                  </Badge>
                  <Box sx={{ textAlign: 'left', display: { xs: 'none', lg: 'block' }, lineHeight: 1.1, minWidth: 0 }}>
                    <Typography component="span" sx={{ fontWeight: 700, display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.name}
                    </Typography>
                    <Typography component="span" variant="caption" color="text.secondary">
                      {roleLabel}
                    </Typography>
                  </Box>
                </Button>
              </Box>
            )}

            {/* Mobile hamburger */}
            <IconButton
              edge="end"
              aria-label="Abrir menu"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <Badge color="error" badgeContent={totalNotifyCount > 9 ? '9+' : totalNotifyCount} invisible={totalNotifyCount === 0}>
                <MenuIcon />
              </Badge>
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {rhItems.map((item) => (
          <MenuItem key={item.to} onClick={() => go(item.to)} sx={{ gap: 2, justifyContent: 'space-between', minWidth: 240 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <span>{item.label}</span>
            </Box>
            {item.notify ? <Chip size="small" color="error" label={item.notify} /> : null}
          </MenuItem>
        ))}
      </Menu>

      {/* User menu (desktop) */}
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{user?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel}
          </Typography>
        </Box>
        <Divider />
        {showGestaoPontoLink && hasGestaoPontoNotify && (
          <MenuItem onClick={() => go(HR_GESTAO_PONTO_LINK)} sx={{ justifyContent: 'space-between', gap: 2 }}>
            Gestão de ponto
            <Chip size="small" color="error" label={totalPending} />
          </MenuItem>
        )}
        {deadlineAlerts.map((alert) => (
          <MenuItem key={deadlineAlertKey(alert)} onClick={() => go(alert.href)} sx={{ justifyContent: 'space-between', gap: 2 }}>
            {alert.title}
            <Chip size="small" color="warning" label={deadlineAlertChipLabel(alert)} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5 }}>
          <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Sair
        </MenuItem>
      </Menu>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation">
          {user && (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar src={userPhotoUrl || undefined} sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>
                {userInitials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {roleLabel}
                </Typography>
              </Box>
            </Box>
          )}
          <Divider />
          <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {mainNav.map((item) => {
              const active = navItemActive(item);
              return (
                <ListItemButton key={item.id} selected={active} onClick={() => go(item.to)}>
                  <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} slotProps={{ primary: { sx: { fontWeight: active ? 700 : 500 } } }} />
                  {item.notify ? <Chip size="small" color="error" label={item.notify} /> : null}
                </ListItemButton>
              );
            })}
            {rhItems.length > 0 && (
              <>
                <ListSubheader disableSticky sx={{ fontWeight: 700, color: 'text.secondary', bgcolor: 'transparent' }}>
                  RH
                </ListSubheader>
                {rhItems.map((item) => {
                  const active = location.pathname.startsWith(item.to.split('?')[0]);
                  return (
                    <ListItemButton key={item.to} selected={active} onClick={() => go(item.to)} sx={{ pl: 3 }}>
                      <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.label} />
                      {item.notify ? <Chip size="small" color="error" label={item.notify} /> : null}
                    </ListItemButton>
                  );
                })}
              </>
            )}
          </List>
          <Divider />
          {user && (
            <Box sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" onClick={handleLogout} startIcon={<LogoutOutlinedIcon />}>
                Sair
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Main */}
      <Box component="main" sx={{ flexGrow: 1, width: '100%' }}>
        {children}
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src="/brand-logo.png" alt="Aplopes Tecnologia" sx={{ height: 28 }} />
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>Feedback</Typography>
                <Typography variant="caption" color="text.secondary">
                  Gestão de performance
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary">
              © {year} Feedback. Avaliações quinzenais e visão consolidada do time.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
